import React, { useEffect, useState } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register necessary components and plugins
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

export default function Visualizations({ selectedNode, otherCommentNodes, isDarkMode }) {

    // Check if otherCommentNodes is not empty and has weights
    const initialWeightType = otherCommentNodes.length > 0 && otherCommentNodes[0].weights
        ? Object.keys(otherCommentNodes[0].weights)[0]
        : null;

    const [selectedWeightType, setSelectedWeightType] = useState(initialWeightType);

    useEffect(() => {
        // Any additional setup can go here
    }, [otherCommentNodes, selectedWeightType]);

    const handleWeightTypeChange = (event) => {
        setSelectedWeightType(event.target.value);
    };

    const calculateWeightSums = () => {
        const weightSums = {};

        otherCommentNodes.forEach((node) => {
            if (node.weights && node.weights[selectedWeightType]) {
                Object.entries(node.weights[selectedWeightType]).forEach(([key, value]) => {
                    if (!weightSums[key]) {
                        weightSums[key] = 0;
                    }
                    weightSums[key] += value;
                });
            }
        });

        return weightSums;
    };

    const calculateAverageWeights = () => {
        const weightSums = calculateWeightSums();
        const nodeCount = otherCommentNodes.length;

        const averageWeights = {};
        Object.entries(weightSums).forEach(([key, sum]) => {
            averageWeights[key] = sum / nodeCount;
        });

        return averageWeights;
    };

    const calculateEmotionDistribution = () => {
        return calculateWeightSums();
    };

    const totalWeights = calculateWeightSums();
    const totalSum = Object.values(totalWeights).reduce((acc, value) => acc + value, 0);

    const averageWeightsData = {
        labels: otherCommentNodes.length > 0 && otherCommentNodes[0].weights[selectedWeightType]
            ? Object.keys(otherCommentNodes[0].weights[selectedWeightType])
            : [],
        datasets: [
            {
                label: 'Average Weights',
                data: Object.values(calculateAverageWeights()),
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
            },
        ],
    };

    const emotionDistributionData = {
        labels: otherCommentNodes.length > 0 && otherCommentNodes[0].weights[selectedWeightType]
            ? Object.keys(otherCommentNodes[0].weights[selectedWeightType])
            : [],
        datasets: [
            {
                data: Object.values(calculateEmotionDistribution()),
                backgroundColor: [
                    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FFFFFF', '#00FFFF', '#FF00FF',
                ],
            },
        ],
    };

    const textColor ='#000';

    const barChartOptions = {
        plugins: {
            layout: {
                padding: {
                    top: 200,
                },
            },
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: "Emotion Distribution",
                color: textColor,
                font: {
                    size: 20,
                },
            },
            datalabels: {
                anchor: 'end',
                align: 'top',
                formatter: (value, context) => {
                    const label = context.chart.data.labels[context.dataIndex];
                    const total = totalWeights[label]?.toFixed(2) || 0;
                    return `${value.toFixed(2)}`;
                },
                color: textColor,
                backgroundColor: '#ccc',
            },
        },
    };

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            title: {
                display: true,
                text: "Emotion Distribution",
                color: textColor,
                font: {
                    size: 20,
                },
            },
            legend: {
                color: textColor,
                position: 'right',
                title: {
                    display: true,
                    color: textColor,
                },
                labels: {
                    color: textColor,
                    font: {
                        size: 16,
                    },
                },
            },
            datalabels: {
                anchor: 'center',
                align: 'end',
                formatter: (value, context) => {
                    const percentage = ((value / totalSum) * 100).toFixed(2);
                    return `${value.toFixed(2)} (${percentage}%)`;
                },
                color: textColor,
                backgroundColor: '#ccc',
                borderRadius: 3,
                padding: 3,
            },
        },
    };

    const totalCommentCount = otherCommentNodes.length;

    return (
        <div
            style={{
                width: '100%',
                border: '1px solid var(--menu-border-color)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0',
                margin: '0'
            }}>
            <div
                style={{
                    width: '100%',
                    maxWidth: '720px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'center',
                    gap: '2rem',
                    padding: '1rem',
                    margin: '0',
                }}>
                {selectedWeightType ? (
                    <>
                        <div>
                            <label htmlFor="weightType"><h3 style={{ margin: '0' }}>Select Weight Type: </h3></label>
                            <select
                                id="weightType"
                                value={selectedWeightType}
                                onChange={handleWeightTypeChange}
                                style={{
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {otherCommentNodes.length > 0 && otherCommentNodes[0].weights
                                    ? Object.keys(otherCommentNodes[0].weights).map((weightType) => (
                                        <option key={weightType} value={weightType}>
                                            {weightType}
                                        </option>
                                    ))
                                    : null}
                            </select>
                        </div>

                        <h3>Total Comment Count: <br />
                            <span style={{ fontSize: '3rem' }}>{totalCommentCount}</span></h3>

                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', border: '1px solid var(--menu-border-color)', borderRadius: '1rem', backgroundColor: '#eee' }}>
                            <div style={{ width: '90%' }}>
                                <Bar data={averageWeightsData} options={barChartOptions} />
                            </div>
                        </div>

                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', border: '1px solid var(--menu-border-color)', borderRadius: '1rem', backgroundColor: '#eee' }}>
                            <div style={{ width: '90%' }}>  
                                <Pie data={emotionDistributionData} options={pieChartOptions} />
                            </div>
                        </div>
                    </>
                ) : (
                    <p>No data available to display.</p>
                )}
            </div>
        </div>
    );
}
